import { useCallback, useEffect, useState } from 'react';
import type { MediaItem, OpsSessionDetail as Detail, SessionPlan } from '@wallflower/shared';
import {
  FAILURE_MESSAGES,
  MAX_EXPIRY_SHIFT_DAYS,
  PLAN_TERMS,
  PURCHASABLE_PLAN,
  SESSION_PLANS,
} from '@wallflower/shared';
import { opsApi } from '../lib/opsApi.ts';
import { formatBytes, formatRelative } from '../lib/format.ts';
import CopyField from './CopyField.tsx';

const DAY_MS = 24 * 60 * 60 * 1000;

interface Props {
  sessionId: string;
  onChanged: () => void;
  onDeleted: () => void;
}

/**
 * The troubleshooting view for one event: its real size on disk, whatever is
 * stuck or broken, and the two blunt instruments - move the expiry, or delete it.
 */
export default function OpsSessionDetail({ sessionId, onChanged, onDeleted }: Props) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await opsApi.sessionDetail(sessionId));
      setError(null);
    } catch {
      setError('Could not load this event.');
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const retry = useCallback(
    async (item: MediaItem) => {
      setBusy(true);
      try {
        await opsApi.retryMedia(item.id);
        await load();
        onChanged();
      } catch {
        setError('That item could not be queued again.');
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged],
  );

  const moveExpiry = useCallback(
    async (expiresAt: number) => {
      setBusy(true);
      try {
        await opsApi.setExpiry(sessionId, expiresAt);
        await load();
        onChanged();
      } catch {
        setError('That expiry was not accepted.');
      } finally {
        setBusy(false);
      }
    },
    [sessionId, load, onChanged],
  );

  const setPlan = useCallback(
    async (plan: SessionPlan) => {
      setBusy(true);
      try {
        await opsApi.setPlan(sessionId, plan);
        await load();
        onChanged();
      } catch {
        setError('That plan change was not accepted.');
      } finally {
        setBusy(false);
      }
    },
    [sessionId, load, onChanged],
  );

  const destroy = useCallback(async () => {
    setBusy(true);
    try {
      await opsApi.deleteSession(sessionId);
      onDeleted();
    } catch {
      setError('Could not delete this event.');
      setBusy(false);
    }
  }, [sessionId, onDeleted]);

  if (!detail) {
    return <p className="mt-3 text-sm text-muted">{error ?? 'Opening…'}</p>;
  }

  const troubled = detail.media.filter((item) => item.status !== 'ready');
  const now = Date.now();
  const planLimit = PLAN_TERMS[detail.session.plan].mediaLimit;

  return (
    <div className="mt-4 grid gap-4 border-t border-edge pt-4">
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <CopyField label="On the TV" value={detail.session.slideshowUrl} />
        <CopyField label="Guest upload page" value={detail.session.uploadUrl} />
      </div>

      <div className="grid gap-2">
        {showAdmin ? (
          <CopyField
            label="Control panel for this event"
            hint="This is the host's own credential. Anyone with it can moderate or delete the event."
            value={detail.adminUrl}
            tone="warn"
          />
        ) : (
          <button
            type="button"
            className="btn btn-ghost justify-self-start px-3 py-2 text-sm"
            onClick={() => setShowAdmin(true)}
          >
            Reveal the host's control panel link
          </button>
        )}
      </div>

      <p className="text-sm text-muted">
        {formatBytes(detail.storageBytes)} on disk, including thumbnails and web copies.{' '}
        {detail.session.messageCount} messages. Created{' '}
        {formatRelative(detail.session.createdAt, now)}.
      </p>

      {/*
        The manual half of billing. It posts to the same applyPlan the Stripe
        webhook uses, so the wall gets the plan's limits and its full term, and
        the TV and the host's panel are told over the hub straight away.
      */}
      <section>
        <h3 className="eyebrow">Plan</h3>
        <p className="mt-1 text-sm text-muted">
          On {PLAN_TERMS[detail.session.plan].label}
          {planLimit === null
            ? ', unlimited photos'
            : `, ${planLimit} ${planLimit === 1 ? 'photo' : 'photos'}`}
          . Changing it here resets the limits and the expiry to that plan's terms, exactly as a
          paid checkout would - no money moves either way.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {SESSION_PLANS.filter((plan) => plan !== detail.session.plan).map((plan) => (
            <button
              key={plan}
              type="button"
              disabled={busy}
              className="btn btn-ghost px-3 py-1.5 text-xs"
              style={plan === PURCHASABLE_PLAN ? { color: 'var(--color-stem)' } : undefined}
              onClick={() => void setPlan(plan)}
            >
              Move to {PLAN_TERMS[plan].label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3 className="eyebrow">Expiry</h3>
        <p className="mt-1 text-sm text-muted">
          Everything is deleted at {new Date(detail.session.expiresAt).toLocaleString()}. Cleanup
          runs hourly.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            className="btn btn-ghost px-3 py-1.5 text-xs"
            onClick={() => void moveExpiry(detail.session.expiresAt + 7 * DAY_MS)}
          >
            + 7 days
          </button>
          <button
            type="button"
            disabled={busy}
            className="btn btn-ghost px-3 py-1.5 text-xs"
            onClick={() => void moveExpiry(detail.session.expiresAt + 30 * DAY_MS)}
          >
            + 30 days
          </button>
          <button
            type="button"
            disabled={busy}
            className="btn btn-ghost px-3 py-1.5 text-xs"
            onClick={() => void moveExpiry(now + 60_000)}
          >
            Expire at the next sweep
          </button>
        </div>
        {detail.session.expiresAt > now + MAX_EXPIRY_SHIFT_DAYS * DAY_MS - 7 * DAY_MS ? (
          <p className="mt-2 text-xs" style={{ color: 'var(--color-sulphur)' }}>
            Expiry cannot be pushed more than {MAX_EXPIRY_SHIFT_DAYS} days out.
          </p>
        ) : null}
      </section>

      <section>
        <h3 className="eyebrow">Not on screen</h3>
        {troubled.length === 0 ? (
          <p className="mt-1 text-sm text-muted">
            Everything uploaded here processed cleanly.
          </p>
        ) : (
          <ul className="mt-2 grid gap-2">
            {troubled.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-edge p-3 text-sm"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    backgroundColor:
                      item.status === 'failed' ? 'var(--color-danger)' : 'var(--color-sulphur)',
                  }}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block truncate">
                    {item.failureReason
                      ? FAILURE_MESSAGES[item.failureReason]
                      : 'Still processing'}
                  </span>
                  <span className="font-mono text-xs text-muted">
                    {item.kind} · {item.uploaderName ?? 'anonymous'} ·{' '}
                    {formatRelative(item.createdAt, now)}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={busy}
                  className="btn btn-ghost ml-auto shrink-0 px-3 py-1.5 text-xs"
                  onClick={() => void retry(item)}
                >
                  Try again
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="eyebrow" style={{ color: 'var(--color-danger)' }}>
          Delete this event
        </h3>
        <p className="mt-1 text-sm text-muted">
          Removes every photo, clip and message, and the files on disk. Type the event name to
          confirm.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            className="field max-w-xs"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={detail.session.name}
            aria-label={`Type ${detail.session.name} to confirm deletion`}
          />
          <button
            type="button"
            disabled={busy || confirmName.trim() !== detail.session.name}
            className="btn btn-ghost px-3 py-2 text-sm"
            style={{ color: 'var(--color-danger)' }}
            onClick={() => void destroy()}
          >
            Delete for good
          </button>
        </div>
      </section>
    </div>
  );
}
